import { userDtos, webDtos } from '@vectronic/openApi-customer';
import { createKMLFile, escapeCsvCell, ExportOptions, Translator } from '@vectronic/util';
import JSZip from 'jszip';
import moment from 'moment';
import { DatatableColumn, DisplayColumn } from '../types/datatable-column';
import { DatatableConfig } from '../types/datatable-configuration';

type Input<T extends object> = {
  data: T[];
  columnOrder: DisplayColumn<T>[];
  datatableConfig: DatatableConfig<T>;
  exportOptions: ExportOptions;
  userPreferences?: userDtos.UserPreferences;
  multipleFileOptions?: {
    groupBy: (row: T) => string | number | undefined;
    titleName: (data: T[], groupId: string) => string;
  };
};

export class TableExport {
  static csv<T extends object>(input: Input<T>) {
    if (!input.data?.length) return;
    const { exportType, isSingleFile } = input.exportOptions;
    if (exportType === 'kml') return this.kml(input.data, input.userPreferences);
    if (exportType !== 'csv') return;
    if (isSingleFile) return this.singleCsv(input);
    else return this.multipleCsv(input);
  }

  static async kml(
    positions: webDtos.GPSPosition[],
    { oldestMarkerColor, newestMarkerColor }: userDtos.UserPreferences,
  ) {
    const momentTag = moment().format('YYYY-MMM-DD_HH-mm-ss');
    const groupedPositions = this.groupBy(positions, ({ idDevice }) => idDevice);
    const devicesIds = Object.keys(groupedPositions).filter(Boolean);
    const files = devicesIds.map((deviceId) => ({
      name: `Positions-${deviceId}-${momentTag}.kml`,
      content: createKMLFile(
        groupedPositions[deviceId],
        deviceId,
        oldestMarkerColor,
        newestMarkerColor,
      ),
    }));
    if (files.length === 1)
      return this.downloadFile(
        files[0].name,
        files[0].content,
        'application/vnd.google-earth.kml+xml',
      );
    const zip = new JSZip();
    for (const { name, content } of files) zip.file(name, content);
    const blob = await zip.generateAsync({ type: 'blob' });
    this.downloadZip(`Positions-${momentTag}.zip`, blob);
  }

  private static titleName(data: any[]) {
    const firstId = data[0].idDevice;
    const haveSameDeviceId = Boolean(firstId) && data.every(({ idDevice }) => idDevice === firstId);
    if (haveSameDeviceId) return `Collar${firstId}-`;
    if (data.length === 1 && data[0].name) return `${data[0].name}-`;
    return '';
  }

  private static singleCsv<T extends object>({
    data,
    columnOrder,
    datatableConfig,
    exportOptions,
  }: Input<T>) {
    const columns = this.columns(columnOrder, datatableConfig.columns);
    if (!columns.length) return;
    const csvContent = this.csvContent(data, columns, exportOptions.csvSeparator);
    const titleName = this.titleName(data).replace(' ', '-');
    const fileName = `${datatableConfig.title.replace(' ', '-')}-${titleName}${moment().format('YYYY-MMM-DD_HH-mm-ss')}.csv`;
    this.downloadFile(fileName, csvContent);
  }

  private static async multipleCsv<T extends object>(input: Input<T>) {
    const { columnOrder, datatableConfig, exportOptions } = input;
    const groupBy =
      input.multipleFileOptions?.groupBy ??
      ((row: T) => (row as T & { idDevice?: string | number }).idDevice);
    const groupedData = this.groupBy(input.data, groupBy);
    const groupIds = Object.keys(groupedData).filter(Boolean);
    if (!groupIds.length) return;
    const momentTag = moment().format('YYYY-MMM-DD_HH-mm-ss');
    const columns = this.columns(columnOrder, datatableConfig.columns);
    if (!columns.length) return;
    const files = groupIds
      .map((groupId) => {
        const data = groupedData[groupId];
        if (!data?.length) return null;
        const content = this.csvContent(data, columns, exportOptions.csvSeparator);
        const titleName = (
          input.multipleFileOptions?.titleName(data, groupId) ?? this.titleName(data)
        ).replace(' ', '-');
        const name = `${datatableConfig.title.replace(' ', '-')}-${titleName}${momentTag}.csv`;
        return { name, content };
      })
      .filter(Boolean);
    if (!files.length) return;
    if (files.length === 1) return this.downloadFile(files[0].name, files[0].content);
    const zip = new JSZip();
    for (const { name, content } of files) zip.file(name, content);
    const blob = await zip.generateAsync({ type: 'blob' });
    this.downloadZip(`${datatableConfig.title.replace(' ', '-')}-${momentTag}.zip`, blob);
  }

  private static columns<T extends object>(
    order: DisplayColumn<T>[],
    columns: DatatableColumn<T>[],
  ) {
    return order
      .map((_fieldName) => columns.find(({ fieldName }) => fieldName === _fieldName))
      .filter((column) => Boolean(column) && !column.disableCsvExport);
  }

  private static csvContent<T extends object>(
    data: T[],
    columns: DatatableColumn<T>[],
    separator: ExportOptions['csvSeparator'],
  ) {
    const header = columns
      .map(({ csvHeader, headerName, fieldName }) =>
        escapeCsvCell(csvHeader ?? headerName ?? Translator.headerName(fieldName), separator),
      )
      .join(separator);
    const body = data
      .map((row) =>
        columns
          .map(({ csvTranslator, translator, fieldName }) => {
            if (csvTranslator) return escapeCsvCell(csvTranslator(row), separator);
            if (translator) return escapeCsvCell(translator(row), separator);
            return escapeCsvCell(row[fieldName as keyof T], separator);
          })
          .join(separator),
      )
      .join('\n');
    return [header, body].join('\n');
  }

  private static downloadFile(filename: string, text: string, mime = 'text/csv;charset=utf-8') {
    const element = document.createElement('a');
    element.setAttribute('href', `data:${mime},` + encodeURIComponent(text));
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  private static downloadZip(filename: string, blob: Blob) {
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.href = url;
    element.download = filename;
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
  }

  private static groupBy<T>(arr: T[], getGroupId: (row: T) => string | number | undefined) {
    return arr.reduce(
      (acc, cur) => {
        const groupId = getGroupId(cur);
        if (groupId === undefined || groupId === null) return acc;
        acc[groupId] = [...(acc[groupId] || []), cur];
        return acc;
      },
      {} as Record<string, T[]>,
    );
  }
}
